import type { CSSProperties, JSX, ReactNode, ComponentPropsWithoutRef } from 'react';
import { styled } from 'styled-components';

type StyledDataListProps = {
  children: ReactNode;
};

const StyledDataList = styled.dl<StyledDataListProps>`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: var(--wui-space-02);
  row-gap: var(--wui-space-04);
  align-items: baseline;
  align-content: start;

  dt {
    margin: 0;
    padding: 0;
    font-weight: var(--wui-datalist-label-prominence);
    width: var(--wui-datalist-label-max-width, auto);
  }

  dd {
    margin: 0;
    padding: 0;
    text-align: var(--wui-datalist-value-alignment, start);
  }
`;

export type DataListProps = ComponentPropsWithoutRef<'dl'> & {
  /**
   * Should only contain `DataListItem` components.
   */
  children: ReactNode;
  /**
   * Maximum width for the label.
   */
  labelMaxWith?: string;
  /**
   * Prominence of the label text. Default is `primary`.
   */
  labelProminence?: 'primary' | 'secondary';
  /**
   * Alignment of the value text. Default is `start`.
   */
  valueAlignment?: 'end' | 'start';
};

/**
 * The DataList component is used to display a list of items with labels and values.
 */
export const DataList = ({
  children,
  valueAlignment = 'start',
  labelMaxWith = 'auto',
  labelProminence = 'primary',
  ...props
}: DataListProps): JSX.Element => {
  return (
    <StyledDataList
      role="list"
      style={
        {
          '--wui-datalist-label-max-width': labelMaxWith,
          '--wui-datalist-value-alignment': valueAlignment === 'end' ? 'right' : 'left',
          '--wui-datalist-label-prominence':
            labelProminence === 'secondary'
              ? 'var(--wui-typography-weight-label)'
              : 'var(--wui-typography-weight-label-bold)',
        } as CSSProperties
      }
      {...props}
    >
      {children}
    </StyledDataList>
  );
};

DataList.displayName = 'DataList';
